/**
 * Croatian overrides for the analytics screens.
 *
 * Only what genuinely differs from Bosnian. Croatian is ijekavica too, so
 * nothing moves for the jat reflex — what changes is vocabulary: *sustav*,
 * *tvrtka*, *datoteka*, *sigurnost*, *tipka*, and borrowed verbs in -irati.
 *
 * On this screen the words that move are the analytics ones: *razdoblje* for
 * *period*, *postotak* for *procenat*, *stupac* for *stubić*, *tjedan* for
 * *sedmica*, *stroj* for *mašina*, *bod* for *poen*, *redak* for *red*, and
 * *provesti* where Bosnian writes *sprovesti*. Add *tko* / *netko* and *što*,
 * and the infinitive where Bosnian takes *da* + present.
 *
 * An entry identical to its Bosnian base is a bug, not a no-op: i18n.test.ts
 * fails on one, because a line that changes nothing is a decision that was
 * reverted in bs.ts without anybody revisiting this file.
 */

import type { AnalyticsSlice } from '../bs/analytics'

export const analytics: Partial<AnalyticsSlice> = {
  'analytics.period.label': 'Razdoblje',

  'analytics.kpi.openNow.hint': '{count} izvan roka',

  'analytics.series.overdue': 'Izvan roka',

  'analytics.overTime.empty': 'Nema podataka za odabrano razdoblje',
  'analytics.timeInStatus.hint':
    'Pokazuje gdje se prijave zadržavaju — najduži stupac je usko grlo procesa.',
  // *stupac*, matching the sentence above it.
  'analytics.timeInStatus.explain':
    'Vrijeme se broji tek kada prijava izađe iz statusa, pa ono što u njemu još stoji ne ulazi ni u jedan stupac.',
  'analytics.timeInStatus.empty.description':
    'Podaci se pojavljuju kada prijave počnu mijenjati status.',

  'analytics.process.firstResponse.hint': 'Koliko prijava čeka da je netko preuzme',
  'analytics.process.implemented': 'Provedeno od prihvaćenih',
  'analytics.process.implemented.hint': 'Prihvaćeno pa nikad provedeno gasi motivaciju',

  'analytics.backlog.hint':
    '„42 otvorene” ne govori ništa. Koliko ih je starije od tjedna — govori.',
  'analytics.backlog.explain':
    'Mjeri se od dana prijave do danas i obuhvaća sve otvorene prijave, a ne samo one iz odabranog razdoblja.',

  'analytics.funnel.hint':
    'Od predloženog do provedenog — i koliko je od procijenjene uštede zaista ostvareno.',
  'analytics.funnel.empty': 'Nema prijedloga u ovom razdoblju',

  'analytics.recurring.title': 'Što se ponavlja',
  'analytics.recurring.hint':
    'Radni centar i vrsta zajedno. „Hoblanje ima 40 prijava” je šum; „Hoblanje ima 19 kvarova” je stroj koji treba pogledati.',
  'analytics.recurring.empty': 'Nema podataka za ovo razdoblje',

  'analytics.contributors.title': 'Najaktivniji zaposlenici',
  'analytics.contributors.hint':
    'Tko najviše prijavljuje i koliko je bodova osvojio u ovom razdoblju.',
  'analytics.contributors.empty': 'Nema prijava u ovom razdoblju',

  'analytics.fiveS.trend.hint':
    'Prikazano u postocima. Montaža se ocjenjuje 1–3, ostali centri 1–5, pa sirovi prosjeci nisu usporedivi.',
  'analytics.fiveS.byWorker.hint':
    'Prosjek radnih centara koje je radnik održavao, po mjesecu. Ocjenjuje se centar, ne radnik — netko tko je preuzeo zapušten centar imat će nizak postotak upravo zato što je poslan da ga sredi.',

  'analytics.fiveS.workers.hidden.one':
    'Prikazano {shown} radnika s najviše evaluacija; još {count} radnik nije prikazan.',
  'analytics.fiveS.workers.hidden.few':
    'Prikazano {shown} radnika s najviše evaluacija; još {count} radnika nisu prikazana.',
  'analytics.fiveS.workers.hidden.many':
    'Prikazano {shown} radnika s najviše evaluacija; još {count} radnika nije prikazano.',

  'analytics.fiveS.byCategory.explain':
    'Prosjek svih odgovora u kategoriji za odabranu godinu, preračunat u postotak skale obrasca — Montaža se ocjenjuje 1–3, ostali centri 1–5, pa sirove ocjene ne stoje na istoj osi.',

  // *Predan*, not *predat* — and *kriterij*, which drops the -um Bosnian keeps.
  'analytics.fiveS.noEvaluations': 'Nema predanih evaluacija',
  'analytics.fiveS.worst.title': 'Kriteriji s najslabijom ocjenom',
  'analytics.fiveS.worst.explain':
    'Poredano po prosjeku za cijelu godinu, od najnižeg — kriterij ocijenjen jednom stoji ovdje pored onog ocijenjenog pedeset puta.',
  'analytics.fiveS.worst.empty': 'Nema predanih evaluacija za ovu godinu',
  'analytics.fiveS.worst.criterion': 'Kriterij',

  'analytics.pager.perPage.label': 'Redaka po stranici',

  // *da li* -> *je li*, *ko* -> *tko*, *porediti* -> *usporediti*.
  'analytics.shifts.subtitle':
    'Je li bitno tko je na smjeni. Ako sve smjene drže stroj jednako, problem je u stroju; ako je razlika velika, problem je u smjeni.',
  'analytics.shifts.explain':
    'Sve ovdje se gradi iz obilazaka na kojima je odabrana smjena; dok smjene ne postoje ili ih obilasci ne nose, nema se što prikazati.',
  'analytics.shifts.spread.hint':
    'Strojevi koje jedna smjena drži bitno bolje od druge. Prosjek to skriva — ovdje se vidi.',
  'analytics.shifts.spread.explain':
    'Razlika je između prosjeka najbolje i najslabije smjene na tom centru, a centar ulazi u popis tek kada su ga u razdoblju obišle najmanje dvije smjene.',
  'analytics.shifts.categories.hint':
    'Ocjene su preračunate u postotke, pa se Montaža (skala 1–3) uspoređuje s ostalim centrima (1–5).',
  'analytics.workforce.hint':
    'Tri odvojene stvari, namjerno bez zbroja. 5S govori o strojevima koje je netko držao, a ne o njemu; prijave govore o inicijativi; zadaci i o tome koliko je posla nekome dodijeljeno. Jedan zbirni broj čitao bi se kao ocjena radnika.',
  'analytics.workforce.rating.note':
    'Prihvaćeni prijedlozi se dodaju na osnovu i mogu je samo podići — odbijen prijedlog ne oduzima ništa. Tko nije bio ni na jednom obilasku ili nema nijedan zadatak, ocjenjuje se samo po onome što ima, a dio koji nedostaje se ne računa kao nula.',
  'analytics.workforce.fiveS.hint':
    'Prosjek svih obilazaka na kojima je radnik upisan; manji broj pored njega je razlika u odnosu na prethodno razdoblje iste duljine.',
  'analytics.workforce.walks.hint':
    'Broj predanih 5S obilazaka na kojima je radnik upisan; ispod {walks}, ocjena nosi oznaku da počiva na malo podataka.',
  'analytics.workforce.accepted.hint':
    'U ocjenu ulazi samo ovaj broj — koliko je prijava poslano i koliko je bodova osvojeno ne mijenja je.',
  'analytics.workforce.tasks.hint':
    'Završeni od onih koji su zaduženi u ovom razdoblju; zadaci iz ranijih razdoblja ne ulaze ni u jedan od ta dva broja.',
  'analytics.workforce.search.hint':
    'U tablici su svi aktivni zaposlenici, i oni bez ijednog podatka u razdoblju, da bi se i tihi mogli primijetiti.',
  'analytics.workforce.role.hint':
    'Uloga ne mijenja način računanja ocjene — svi se računaju po istoj formuli, pa ovaj odabir samo sužava popis.',
  // *filtar* declines to *filtra*, where Bosnian keeps *filtera*.
  'analytics.workforce.workCenter.hint':
    'Bira ljude raspoređene na taj centar; redni broj pored ocjene ostaje mjesto u cijelom pogonu, jer se rang računa prije filtra.',
  // *definirane*, *ovdje* is shared, *stroj* for a machine.
  'analytics.shifts.none.title': 'Smjene još nisu definirane',
  'analytics.shifts.none.description':
    'Kada dodate smjene, evaluator ih bira pri 5S obilasku, a ovdje se pojavljuje usporedba po smjenama — koja smjena drži koji stroj bolje.',
}
